import { useEffect, useState } from "react";
import { ApiError, api } from "@/lib/api";
import Modal from "@/ui/Modal";
import { useToast } from "@/ui/Toast";

type Props = {
  open: boolean;
  onClose: () => void;
  cliente: { id: number; nombre: string };
  onSaved: () => void;
};

/** Modal compacto para renombrar una empresa (cliente). Solo expone el
 * campo nombre — el resto de datos del cliente se gestionan en otros
 * flujos (contactos, etc.). El backend regenera slug_fs internamente
 * cuando cambia el nombre.
 */
export default function EditarEmpresaModal({
  open,
  onClose,
  cliente,
  onSaved,
}: Props) {
  const toast = useToast();
  const [nombre, setNombre] = useState(cliente.nombre);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setNombre(cliente.nombre);
  }, [open, cliente.nombre]);

  async function guardar() {
    const trimmed = nombre.trim();
    if (!trimmed) {
      toast.error("El nombre no puede quedar vacío");
      return;
    }
    if (trimmed === cliente.nombre) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/api/clientes/${cliente.id}`, { nombre: trimmed });
      toast.success("Empresa renombrada", trimmed);
      onSaved();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude renombrar", msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title="Renombrar empresa"
      description="El nuevo nombre se refleja en todos los casos del cliente."
      size="sm"
      footer={
        <>
          <button onClick={onClose} className="btn-ghost" disabled={saving}>
            Cancelar
          </button>
          <button
            onClick={guardar}
            disabled={saving || !nombre.trim()}
            className="btn-primary disabled:opacity-50"
          >
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </>
      }
    >
      <div>
        <label className="text-xs uppercase tracking-wide text-zinc-400">
          Nombre
        </label>
        <input
          type="text"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          className="input mt-1 w-full"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter") void guardar();
          }}
        />
      </div>
    </Modal>
  );
}
